<template>
  <PageWrapper
    :active-section="SECTION_SETTINGS"
    :active-page="PAGE_SOCIAL_NETWORK_SETTINGS"
    title="Social Networks"
    :breadcrumbs="settingsBreadcrumbs"
    page-name="Social Networks"
    align="left"
    width="full"
  >
    <template #action-button>
      <iDropdown
        :show-dropdown="showSocialNetworksDropdown"
        :transport-to-body="true"
        :z-index="3"
        :max-height="420"
        overlay-position="bottom-right"
        @close="showSocialNetworksDropdown=false"
      >
        <template #trigger>
          <iButton variant="primary" data-test="add-network" @click="showSocialNetworksDropdown = true">
            + Add network
          </iButton>
        </template>
        <template #overlay>
          <iColumn gap="none" :style-overrides="NETWORK_MENU_STYLE">
            <template v-for="socialNetwork in socialNetworkCards" :key="socialNetwork.social_network_name">
              <iRow
                vertical-align="middle"
                wrap="nowrap"
                gap="standard"
                cursor="pointer"
                :style-overrides="NETWORK_OPTION_STYLE"
                :data-test="`network-option-${socialNetwork.social_network_name}`"
                @click="connectSocialNetwork(socialNetwork.social_network_name)"
              >
                <iIcon
                  :icon="logoIconFor(socialNetwork.social_network_name)"
                  :name="logoIconFor(socialNetwork.social_network_name)"
                  size="standard"
                  disable-hover
                />
                <iColumn gap="none" width="fill">
                  <iText>{{ socialNetwork.social_network_name }}</iText>
                  <iText v-if="networkOptionSubline(socialNetwork)" font-size="small" variant="subtle">
                    {{ networkOptionSubline(socialNetwork) }}
                  </iText>
                </iColumn>
                <iIcon
                  v-if="statusIcon(socialNetwork)"
                  :icon="statusIcon(socialNetwork).icon"
                  :variant="statusIcon(socialNetwork).variant"
                  :tooltip="statusIcon(socialNetwork).tooltip"
                  size="small"
                  disable-hover
                  :data-test="`network-health-${socialNetwork.social_network_name}`"
                />
              </iRow>
            </template>
          </iColumn>
        </template>
      </iDropdown>
    </template>
    <iColumn gap="large">
      <iAlert
        v-if="connectResultPresentation"
        :variant="connectResultPresentation.variant"
        :title="connectResultPresentation.title"
        data-test="connect-result"
      >
        <iRow wrap="nowrap" vertical-align="middle">
          <iColumn gap="extraSmall" width="fill">
            <iText>
              {{ connectResultPresentation.text }}
            </iText>
            <iText
              v-if="connectResultPresentation.technicalDetails"
              font-size="small"
              data-test="connect-result-details"
            >
              {{ networkSaid(connectResult.network, connectResultPresentation.technicalDetails) }}
            </iText>
          </iColumn>
          <iButton
            size="extraSmall"
            variant="secondary"
            data-test="dismiss-connect-result"
            @click="dismissConnectResult"
          >
            Dismiss
          </iButton>
        </iRow>
      </iAlert>
      <iAlert
        v-if="pageError"
        variant="error"
        title="Something went wrong"
        data-test="page-error"
      >
        <iRow wrap="nowrap" vertical-align="middle">
          <iText width="fill">
            {{ pageError }}
          </iText>
          <iButton size="extraSmall" variant="secondary" @click="dismissPageError">
            Dismiss
          </iButton>
        </iRow>
      </iAlert>

      <iRow vertical-align="middle" wrap="nowrap">
        <iColumn gap="none" width="fill">
          <iSubHeading>My Connections</iSubHeading>
          <iText variant="subtle" font-size="small" data-test="connections-summary">
            {{ connectionsSummary }}
          </iText>
        </iColumn>
      </iRow>

      <iCard v-if="!visibleSocialNetworks.length" width="fill" data-test="connections-empty">
        <template #body>
          <iColumn gap="small" :style-overrides="CARD_BODY_STYLE">
            <iText font-weight="dark">
              No accounts connected yet
            </iText>
            <iText variant="subtle">
              Connect an account and it will show up here, ready to publish to from your stories.
            </iText>
          </iColumn>
        </template>
      </iCard>

      <iColumn v-else gap="standard" width="fill">
        <iCard
          v-for="account in visibleSocialNetworks"
          :key="account.social_network_account_id"
          width="fill"
          :data-test="`account-card-${account.social_network_name}`"
        >
          <template #body>
            <iRow
              wrap="nowrap"
              vertical-align="top"
              gap="large"
              width="fill"
              :style-overrides="CARD_BODY_STYLE"
            >
              <iAvatar :name="account.account_name || account.social_network_name" :src="account.avatar_url || ''" size="large" />
              <iColumn gap="extraSmall" width="fill">
                <iRow vertical-align="middle" wrap="nowrap" gap="small">
                  <iText font-weight="dark">
                    {{ account.account_name || account.social_network_name }}
                  </iText>
                  <iRow
                    vertical-align="middle"
                    wrap="nowrap"
                    gap="extraSmall"
                    width="hug"
                  >
                    <iIcon
                      :icon="logoIconFor(account.social_network_name)"
                      :name="logoIconFor(account.social_network_name)"
                      size="small"
                      disable-hover
                    />
                    <iText variant="subtle" font-size="small">
                      {{ account.social_network_name }}
                    </iText>
                  </iRow>
                </iRow>
                <iRow
                  vertical-align="middle"
                  wrap="nowrap"
                  gap="extraSmall"
                  :data-test="`account-status-${account.social_network_name}`"
                >
                  <iIcon
                    :icon="statusIcon(account).icon"
                    :variant="statusIcon(account).variant"
                    size="small"
                    disable-hover
                  />
                  <iText font-size="small" :wrap="false" :variant="statusTextVariant(account)">
                    {{ statusPresentation(account).label }}
                  </iText>
                </iRow>
                <iText v-if="statusPresentation(account).reason" font-size="small">
                  {{ statusPresentation(account).reason }}
                </iText>
                <iText v-if="statusPresentation(account).detail" font-size="small" variant="subtle">
                  {{ statusPresentation(account).detail }}
                </iText>
                <template v-if="statusPresentation(account).technicalDetails">
                  <iLink
                    font-size="small"
                    :data-test="`toggle-details-${account.social_network_name}`"
                    @click="toggleTechnicalDetails(account.social_network_account_id)"
                  >
                    {{ isShowingTechnicalDetails(account.social_network_account_id) ? "Hide details" : "Show details" }}
                  </iLink>
                  <iText
                    v-if="isShowingTechnicalDetails(account.social_network_account_id)"
                    font-size="small"
                    variant="subtle"
                    :data-test="`technical-details-${account.social_network_name}`"
                  >
                    {{ technicalDetailsText(statusPresentation(account).technicalDetails) }}
                  </iText>
                </template>
                <iText
                  v-if="accountErrors[account.social_network_account_id]"
                  font-size="small"
                  variant="error"
                  :data-test="`account-error-${account.social_network_name}`"
                >
                  {{ accountErrors[account.social_network_account_id] }}
                </iText>
              </iColumn>
              <iRow
                gap="small"
                wrap="nowrap"
                width="hug"
                vertical-align="middle"
                :data-test="`account-actions-${account.social_network_name}`"
              >
                <iButton
                  v-if="needsReconnect(account)"
                  variant="primary"
                  size="small"
                  :disabled="isAccountPending(account.social_network_account_id)"
                  @click="connectSocialNetwork(account.social_network_name)"
                >
                  Reconnect
                </iButton>
                <iButton
                  variant="secondary"
                  size="small"
                  :disabled="isAccountPending(account.social_network_account_id)"
                  :is-loading="isAccountPending(account.social_network_account_id)"
                  @click="requestDisconnect(account)"
                >
                  {{ needsReconnect(account) ? "Remove" : "Disconnect" }}
                </iButton>
              </iRow>
            </iRow>
          </template>
        </iCard>
      </iColumn>
    </iColumn>
    <iModal
      v-if="isDisconnectModalVisible"
      :title="`Disconnect ${disconnectAccountLabel}?`"
      :max-width="520"
      primary-action-button-label="Disconnect"
      secondary-action-button-label="Cancel"
      :close-on-primary="false"
      data-test="disconnect-confirm-modal"
      @close="closeDisconnectModal"
      @click:secondary="closeDisconnectModal"
      @click:primary="confirmDisconnect"
    >
      <template #body>
        <iColumn gap="large">
          <iText>
            This account is linked to these destinations:
          </iText>
          <iColumn gap="extraSmall" data-test="disconnect-linked-publications">
            <iText
              v-for="publication in linkedPublications"
              :key="publication.publication_id"
              font-weight="dark"
            >
              {{ publication.publication_name }}
            </iText>
          </iColumn>
          <iText variant="subtle">
            Disconnecting removes it from them too, and stories on those destinations will no
            longer publish to this account.
          </iText>
        </iColumn>
      </template>
    </iModal>
    <iPromptModal
      v-if="isAddSocialNetworkModalVisible"
      :title="`Add ${currentSocialNetowork} Connection`"
      message="Simply enter your account name and we will save this to your account."
      button-text="Connect"
      label="Account Handle"
      @close="closeAndResetAddConnectionModal"
      @submit="manualConnectSocialNetwork"
    />
    <iModal
      v-if="isCredentialConnectModalVisible"
      :title="`Add ${currentSocialNetowork} Connection`"
      :max-width="520"
      primary-action-button-label="Connect"
      secondary-action-button-label="Cancel"
      :primary-action-button-loading="isCredentialConnecting"
      :primary-action-button-disabled="!credentialHandle || !credentialAppPassword"
      :close-on-primary="false"
      @close="closeAndResetCredentialConnectModal"
      @click:secondary="closeAndResetCredentialConnectModal"
      @click:primary="credentialConnectSocialNetwork"
    >
      <template #body>
        <iColumn gap="large">
          <iText variant="subtle">
            Enter your handle and an app password. Generate an app password in BlueSky under
            Settings → Privacy and Security → App passwords — don't use your main password.
          </iText>
          <iTextInput
            v-model="credentialHandle"
            label="Account Handle"
            placeholder="e.g. username.bsky.social"
            width="fill"
          />
          <iTextInput
            v-model="credentialAppPassword"
            label="App Password"
            type="password"
            width="fill"
            @enter="credentialConnectSocialNetwork"
          />
          <iText v-if="credentialConnectError" variant="error" data-test="credential-connect-error">
            {{ credentialConnectError }}
          </iText>
        </iColumn>
      </template>
    </iModal>
  </PageWrapper>
</template>

<script>
import PageWrapper from "@/components/PageWrapper";
import { SECTION_SETTINGS, PAGE_SOCIAL_NETWORK_SETTINGS } from "@/constants/nav-constants";
import { mapActions, mapState } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import {
  CREDENTIAL_CONNECT_SOCIAL_NETWORKS,
  FALLBACK_NETWORK_ICON,
  OAUTH_CONNECT_SOCIAL_NETWORKS,
  SOCIAL_NETWORK_LOGO_ICONS,
} from "@/constants/social-network-constants";
import {
  connectResultPresentation,
  connectionsSummary,
  isVisibleAccount,
  needsReconnect,
  statusIcon,
  statusPresentation,
} from "@/helpers/social-network-status-helper";

const NETWORK_MENU_STYLE = {
  paddingTop: "extraSmall",
  paddingBottom: "extraSmall",
};

const NETWORK_OPTION_STYLE = {
  paddingTop: "small",
  paddingBottom: "small",
  paddingLeft: "standard",
  paddingRight: "standard",
  hoverBackgroundColor: "subtle",
};

const CARD_BODY_STYLE = {
  paddingTop: "standard",
  paddingBottom: "standard",
  paddingLeft: "large",
  paddingRight: "large",
};

export default {
  name: "SocialNetworks",
  components: {
    PageWrapper,
  },
  props: {},
  data() {
    return {
      SECTION_SETTINGS,
      PAGE_SOCIAL_NETWORK_SETTINGS,
      NETWORK_MENU_STYLE,
      NETWORK_OPTION_STYLE,
      CARD_BODY_STYLE,
      isAddSocialNetworkModalVisible: false,
      isCredentialConnectModalVisible: false,
      currentSocialNetowork: "",
      credentialHandle: "",
      credentialAppPassword: "",
      showSocialNetworksDropdown: false,
      technicalDetailsShownFor: [],
      isDisconnectModalVisible: false,
      accountPendingDisconnect: null,
    };
  },
  computed: {
    ...mapState(useGeneralStore, [
      "orgId",
      "settingsBreadcrumbs",
    ]),
    ...mapState(useSocialNetworksStore, [
      "socialNetworks",
      "connectResult",
      "pageError",
      "accountErrors",
      "credentialConnectError",
      "isCredentialConnecting",
      "isAccountPending",
      "linkedPublications",
      "hasLinkedPublicationsError",
    ]),
    connectResultPresentation() {
      return connectResultPresentation(this.connectResult);
    },
    socialNetworkCards() {
      const byNetwork = new Map();
      this.socialNetworks.forEach(socialNetwork => {
        if (!byNetwork.has(socialNetwork.social_network_name)) {
          byNetwork.set(socialNetwork.social_network_name, socialNetwork);
        }
      });
      return [...byNetwork.values()].sort((a, b) => a.social_network_name.localeCompare(b.social_network_name));
    },
    visibleSocialNetworks() {
      return this.socialNetworks.filter(isVisibleAccount);
    },
    connectionsSummary() {
      return connectionsSummary(this.visibleSocialNetworks);
    },
    disconnectAccountLabel() {
      const account = this.accountPendingDisconnect;
      return account ? account.account_name || account.social_network_name : "";
    },
  },
  methods: {
    ...mapActions(useSocialNetworksStore, [
      "connect",
      "disconnect",
      "manualConnect",
      "dismissConnectResult",
      "dismissPageError",
      "clearCredentialConnectError",
      "loadLinkedPublications",
      "clearLinkedPublications",
    ]),
    async requestDisconnect(account) {
      await this.loadLinkedPublications(account.social_network_account_id);

      // Never disconnect on a failed lookup: the card already carries the error,
      // and going ahead would silently drop destinations we could not read.
      if (this.hasLinkedPublicationsError) {
        return;
      }

      if (!this.linkedPublications.length) {
        this.disconnect(account, this.orgId);
        return;
      }

      this.accountPendingDisconnect = account;
      this.isDisconnectModalVisible = true;
    },
    confirmDisconnect() {
      const account = this.accountPendingDisconnect;
      this.closeDisconnectModal();
      this.disconnect(account, this.orgId);
    },
    closeDisconnectModal() {
      this.isDisconnectModalVisible = false;
      this.accountPendingDisconnect = null;
      this.clearLinkedPublications();
    },
    isVisibleAccount,
    needsReconnect,
    statusIcon,
    statusPresentation,
    statusTextVariant(account) {
      const variant = statusPresentation(account).variant;
      return variant === "success" ? "standard" : variant;
    },
    networkOptionSubline(socialNetwork) {
      if (!isVisibleAccount(socialNetwork)) {
        return "";
      }
      const handle = socialNetwork.account_name ? ` as ${socialNetwork.account_name}` : "";
      return needsReconnect(socialNetwork) ? `Needs reconnect${handle}` : `Connected${handle}`;
    },
    networkSaid(socialNetworkName, technicalDetails) {
      return `${socialNetworkName} said: ${technicalDetails}`;
    },
    technicalDetailsText(technicalDetails) {
      return `Technical details: ${technicalDetails}`;
    },
    isShowingTechnicalDetails(accountId) {
      return this.technicalDetailsShownFor.includes(accountId);
    },
    toggleTechnicalDetails(accountId) {
      if (this.isShowingTechnicalDetails(accountId)) {
        this.technicalDetailsShownFor = this.technicalDetailsShownFor.filter(id => id !== accountId);
        return;
      }
      this.technicalDetailsShownFor = [...this.technicalDetailsShownFor, accountId];
    },
    logoIconFor(socialNetworkName) {
      return SOCIAL_NETWORK_LOGO_ICONS[socialNetworkName] || FALLBACK_NETWORK_ICON;
    },
    openModal(socialNetwork) {
      this.isAddSocialNetworkModalVisible = true;
      this.currentSocialNetowork = socialNetwork;
    },
    connectSocialNetwork(socialNetwork) {
      this.showSocialNetworksDropdown = false;
      if (CREDENTIAL_CONNECT_SOCIAL_NETWORKS.includes(socialNetwork)) {
        this.openCredentialConnectModal(socialNetwork);
        return;
      }
      if (OAUTH_CONNECT_SOCIAL_NETWORKS.includes(socialNetwork)) {
        this.connect(socialNetwork, this.orgId);
        return;
      }
      this.openModal(socialNetwork);
    },
    manualConnectSocialNetwork(handle) {
      this.manualConnect(this.currentSocialNetowork, this.orgId, handle);
      this.closeAndResetAddConnectionModal();
    },
    closeAndResetAddConnectionModal() {
      this.isAddSocialNetworkModalVisible = false;
      this.currentSocialNetowork = "";
    },
    openCredentialConnectModal(socialNetwork) {
      this.clearCredentialConnectError();
      this.isCredentialConnectModalVisible = true;
      this.currentSocialNetowork = socialNetwork;
    },
    async credentialConnectSocialNetwork() {
      const connected = await this.manualConnect(this.currentSocialNetowork, this.orgId, this.credentialHandle, this.credentialAppPassword);
      if (connected) {
        this.closeAndResetCredentialConnectModal();
      }
    },
    closeAndResetCredentialConnectModal() {
      this.isCredentialConnectModalVisible = false;
      this.currentSocialNetowork = "";
      this.credentialHandle = "";
      this.credentialAppPassword = "";
      this.clearCredentialConnectError();
    },
  },
};
</script>
